// FirebaseNewsCard.js
// Card-based news feed — favicon logo + domain at bottom-right, time at bottom-left.
// Mobile-first grid: 1 col → 2 col → 3 col.

import React, { useState, useMemo } from 'react';
import { Spinner, Alert } from 'react-bootstrap';
import { useMarketData } from '../../hooks/useMarketData';
import './FirebaseNewsCard.css';

// ── Accent colour (left border) keyed by source ─────────────────────────────
const PALETTE = [
  '#3871E0', '#0891b2', '#16a34a', '#d97706',
  '#9333ea', '#dc2626', '#0369a1', '#b45309',
];

function accentForSource(source) {
  if (!source) return PALETTE[0];
  let h = 0;
  for (let i = 0; i < source.length; i++) h = source.charCodeAt(i) + ((h << 5) - h);
  return PALETTE[Math.abs(h) % PALETTE.length];
}

// ── Domain extraction from article URL ──────────────────────────────────────
function getDomain(link) {
  if (!link) return null;
  try {
    return new URL(link).hostname.replace(/^www\./, '');
  } catch {
    return null;
  }
}

// ── Time formatting ──────────────────────────────────────────────────────────
// Parses "07 May 2026 09:30 IST" → "9:30 AM", "Yesterday · 9:30 AM", "07 May"
const MONTHS = {
  jan:0,feb:1,mar:2,apr:3,may:4,jun:5,
  jul:6,aug:7,sep:8,oct:9,nov:10,dec:11,
};

function parsePublished(str) {
  if (!str) return null;
  const d = str.match(/(\d{1,2})\s+(\w{3})\s+(\d{4})/i);
  const t = str.match(/(\d{1,2}):(\d{2})(?::\d{2})?\s*IST/i);
  if (!d) return null;
  const month = MONTHS[d[2].toLowerCase()];
  if (month === undefined) return null;
  return { day: +d[1], month, year: +d[3], hour: t ? +t[1] : null, min: t ? t[2] : null };
}

function to12h(h, m) {
  const ampm = h >= 12 ? 'PM' : 'AM';
  return `${h % 12 || 12}:${m} ${ampm}`;
}

function formatPublished(published) {
  const p = parsePublished(published);
  if (!p) return published || '';

  const now   = new Date();
  const timeStr = p.hour !== null ? to12h(p.hour, p.min) : '';

  if (p.day === now.getDate() && p.month === now.getMonth() && p.year === now.getFullYear()) {
    return timeStr || 'Today';
  }
  const yest = new Date(now);
  yest.setDate(now.getDate() - 1);
  if (p.day === yest.getDate() && p.month === yest.getMonth() && p.year === yest.getFullYear()) {
    return timeStr ? `Yesterday · ${timeStr}` : 'Yesterday';
  }
  const mon = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][p.month];
  return timeStr ? `${p.day} ${mon} · ${timeStr}` : `${p.day} ${mon}`;
}

// ── Individual card ──────────────────────────────────────────────────────────
const NewsCard = ({ article }) => {
  const accent  = accentForSource(article.source);
  const timeStr = formatPublished(article.published);
  const domain  = getDomain(article.link);
  const favicon = domain
    ? `https://www.google.com/s2/favicons?domain=${domain}&sz=32`
    : null;

  return (
    <a
      href={article.link}
      target="_blank"
      rel="noopener noreferrer"
      className="fnc-card"
      style={{ '--accent': accent }}
    >
      {/* Headline */}
      <p className="fnc-title">{article.title}</p>

      {/* Optional summary */}
      {article.summary && (
        <p className="fnc-summary">{article.summary}</p>
      )}

      {/* Footer: time (left) + logo/domain (right) */}
      <div className="fnc-footer">
        {timeStr && <span className="fnc-time">{timeStr}</span>}

        <div className="fnc-source-logo">
          {favicon && (
            <img
              src={favicon}
              alt={domain}
              className="fnc-favicon"
              onError={(e) => { e.currentTarget.style.display = 'none'; }}
            />
          )}
          {domain && <span className="fnc-domain">{domain}</span>}
        </div>
      </div>
    </a>
  );
};

// ── Main component ───────────────────────────────────────────────────────────
const INITIAL_COUNT = 6;

const FirebaseNewsCard = () => {
  const { data, loading, error } = useMarketData();
  const [showAll, setShowAll] = useState(false);

  const allArticles = useMemo(() => data?.news?.data || [], [data]);
  const displayed   = showAll ? allArticles : allArticles.slice(0, INITIAL_COUNT);
  const remaining   = allArticles.length - INITIAL_COUNT;

  if (loading) {
    return (
      <div className="fnc-loading">
        <Spinner animation="border" size="sm" className="me-2" style={{ color: '#3871E0' }} />
        <span>Loading news…</span>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="warning" className="mb-0 small">
        Unable to load news: {error}
      </Alert>
    );
  }

  if (allArticles.length === 0) {
    return (
      <p className="text-muted text-center py-4 mb-0 small">
        No news available for today.
      </p>
    );
  }

  return (
    <div className="fnc-wrapper">
      <div className="fnc-list">
        {displayed.map((article, idx) => (
          <NewsCard key={article.link || idx} article={article} />
        ))}
      </div>

      {!showAll && remaining > 0 && (
        <button className="fnc-show-more" onClick={() => setShowAll(true)}>
          Show {remaining} more article{remaining !== 1 ? 's' : ''} ↓
        </button>
      )}
      {showAll && allArticles.length > INITIAL_COUNT && (
        <button className="fnc-show-more" onClick={() => setShowAll(false)}>
          Show less ↑
        </button>
      )}
    </div>
  );
};

export default FirebaseNewsCard;
